import React, { useContext, useEffect, useMemo, useState } from "react";
import { IntlContext } from "context";
import RegisterProviderCategories from "./Categories";
import RegisterProviderArea from "./Area";
import RegisterProviderInfosIdentity from "./InfosIdentity";
import RegisterProviderInfosProfile from "./InfosProfile";
import RegisterProviderPhoto from "./Photo";
import RegisterProviderCesu from "./Cesu";
import RegisterProviderIban from "./Iban";
import Welcome from "./Welcome";
import RegisterStepper, { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterCsrfRefresh } from "../common/useRegisterCsrfRefresh";
import { useProviderRegisterSteps } from "../common/useProviderRegisterSteps";
import type { RegisterDraft } from "../types";

const PROVIDER_DRAFT_STORAGE_KEY = "twoghether:register:provider:draft:v1";
const PROVIDER_STEP_STORAGE_KEY = "twoghether:register:provider:step:v1";

function readLocalStorageJson<T>(key: string): T | undefined {
    if (typeof window === "undefined") return undefined;
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return undefined;
        return JSON.parse(raw) as T;
    } catch {
        return undefined;
    }
}

function writeLocalStorageJson(key: string, value: unknown) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore storage quota / privacy mode errors
    }
}

function removeLocalStorageItem(key: string) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

const ProviderSteps: React.FC<{
    provider: RegisterDraft | undefined;
    setProvider: (provider: RegisterDraft | undefined) => void;
    clearProviderDraft: () => void;
}> = ({ provider, setProvider, clearProviderDraft }) => {
    const stepper = useRegisterStepper();

    switch (stepper.activeKey) {
        case "prestations":
            return <RegisterProviderCategories provider={provider} setProvider={setProvider} />;
        case "intervention":
            return <RegisterProviderArea provider={provider} setProvider={setProvider} />;
        case "informations":
            return <RegisterProviderInfosIdentity provider={provider} setProvider={setProvider} />;
        case "informations_profile":
            return <RegisterProviderInfosProfile provider={provider} setProvider={setProvider} />;
        case "photo":
            return <RegisterProviderPhoto provider={provider} setProvider={setProvider} />;
        case "cesu":
            return <RegisterProviderCesu provider={provider} setProvider={setProvider} />;
        case "iban":
            return <RegisterProviderIban provider={provider} setProvider={setProvider} />;
        case "confirmation":
            return <Welcome provider={provider} onClearDraft={clearProviderDraft} />;
        default:
            return <RegisterProviderCategories provider={provider} setProvider={setProvider} />;
    }
};

type RegisterProviderModuleProps = {
    onClose?: () => void;
};

const RegisterProviderModule: React.FC<RegisterProviderModuleProps> = ({ onClose }) => {
    const intl = useContext(IntlContext);
    const steps = useProviderRegisterSteps(intl);
    useRegisterCsrfRefresh();

    const [provider, setProvider] = useState<RegisterDraft | undefined>(() =>
        readLocalStorageJson<RegisterDraft>(PROVIDER_DRAFT_STORAGE_KEY)
    );

    const initialStepKey = useMemo(() => {
        const stored = readLocalStorageJson<string>(PROVIDER_STEP_STORAGE_KEY);
        return typeof stored === "string" && stored.trim() ? stored : "prestations";
    }, []);

    useEffect(() => {
        if (provider == null) {
            removeLocalStorageItem(PROVIDER_DRAFT_STORAGE_KEY);
            return;
        }
        writeLocalStorageJson(PROVIDER_DRAFT_STORAGE_KEY, provider);
    }, [provider]);

    return (
        <RegisterStepper
            title="Create your provider profile"
            type="provider"
            hero={{
                imageSrc: "/images/bg-register.png",
                brandText: "Twoghether"
            }}
            steps={steps}
            initialStepKey={initialStepKey}
            className="overflow-y-auto"
            onClose={onClose}
            onStepChange={(key) => writeLocalStorageJson(PROVIDER_STEP_STORAGE_KEY, key)}
        >
            <ProviderSteps
                provider={provider}
                setProvider={setProvider}
                clearProviderDraft={() => setProvider(undefined)}
            />
        </RegisterStepper>
    );
};

export default RegisterProviderModule;
